<template>
  <section
    class="relative rounded bg-white p-4 shadow"
    :class="!small && 'lg:p-8'"
    role="complementary"
    :aria-labelledby="headingId"
  >
    <div>
      <WelcomeIcon
        class="float-left mr-4 mb-4 h-auto w-18 md:w-30"
        :class="!small && 'lg:mr-8 lg:mb-8 lg:w-68'"
      />

      <AppSubHeading :id="headingId">
        {{ welcomeText }}
      </AppSubHeading>
    </div>

    <div
      class="content-message text-sm leading-5 whitespace-normal md:text-base md:leading-5.5"
      :class="!small && 'lg:leading-6'"
      v-html="text"
    />

    <div class="clear-left" />

    <button
      class="absolute top-4 right-4 cursor-pointer rounded text-2xl hover:text-body-60 focus:ring-2 focus:ring-main-40 focus:ring-offset-2 focus:outline-none"
      :aria-label="t('actions.close')"
      type="button"
      @click="$emit('close')"
    >
      <font-awesome-icon :icon="faTimes" />
    </button>
  </section>
</template>

<script lang="ts" setup>
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { generateUniqueId } from '../../utils';
import { AppSubHeading } from '../typography';
import WelcomeIcon from './WelcomeIcon.vue';

const { t } = useI18n();

/**
 * WelcomeMessage displays a personalized welcome message with user information
 * and a closeable interface for onboarding or informational content.
 *
 * Uses internal i18n for welcome text and accessibility labels:
 * - Welcome message: homePage.welcome with firstName/lastName interpolation
 * - Close button aria-label: actions.close
 * - WelcomeIcon manages its own aria-label internally
 */

export interface WelcomeMessageProps {
  /** User's first name for personalization */
  firstName: string;
  /** User's last name for personalization */
  lastName: string;
  /** HTML content of the welcome message */
  text: string;
  /** Whether to display in small/compact mode */
  small?: boolean;
}

const props = withDefaults(defineProps<WelcomeMessageProps>(), {
  small: false,
});

const welcomeText = computed(() => {
  return t('homePage.welcome', {
    firstName: props.firstName,
    lastName: props.lastName,
  });
});

const headingId = generateUniqueId('welcome-heading');

defineEmits<{
  /** Emitted when the close button is clicked */
  (e: 'close'): void;
}>();
</script>
